/**
 * PickAModelPanel — Mode-A ("pick-a-model", no calculator) for SS-tank + DHP.
 *
 * Karan-locked (DHP doc §4 / SS-tank handoff item 3): the rep selects a SKU
 * (SS-tank: capacity+grade embedded) OR a model/Series (DHP) + qty, optionally
 * states the customer profile (DHP — drives the over-ceiling warning), then
 * Converts to a proposal. No calculator, no meeting session (anytime from a
 * lead). Over-ceiling = WARN, never block.
 */
import { useEffect, useState } from 'react';
import {
  ssTankApi,
  domesticHpApi,
  type SsTankPreselectOption,
  type DhpPreselectOption,
  type PreselectOutcome,
} from '@/api/calculators';
import { ConvertToProposalButton } from './ConvertToProposalButton';

type Calc = 'ss_tank' | 'domestic_heat_pump';

const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 };
const inp: React.CSSProperties = { padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 };

export function PickAModelPanel(props: {
  calc: Calc;
  leadId?: string | null;
  dealId?: string | null;
  customerName?: string;
}) {
  const { calc, leadId, dealId, customerName } = props;
  const [options, setOptions] = useState<Array<SsTankPreselectOption | DhpPreselectOption>>([]);
  const [selection, setSelection] = useState('');
  const [qty, setQty] = useState(1);
  const [profile, setProfile] = useState<Record<string, unknown>>({
    family_members: 4, washrooms: 4, covered_area: 4000, area_unit: 'sqft',
    usage_intensity: 'medium', climate_zone: 'north', kitchen_connected: false, jacuzzi: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<PreselectOutcome | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const opts = calc === 'ss_tank'
          ? await ssTankApi.preselectOptions()
          : await domesticHpApi.preselectOptions();
        if (!alive) return;
        setOptions(opts as any);
        if (opts.length) setSelection(calc === 'ss_tank'
          ? (opts[0] as SsTankPreselectOption).sku
          : (opts[0] as DhpPreselectOption).value);
      } catch {
        if (alive) setError('Could not load the model list.');
      }
    })();
    return () => { alive = false; };
  }, [calc]);

  async function handleCreate() {
    if (!selection) { setError('Pick a model first.'); return; }
    setError(null); setLoading(true); setOutcome(null);
    try {
      const res = calc === 'ss_tank'
        ? await ssTankApi.preselect({ sku: selection, qty, leadId, dealId })
        : await domesticHpApi.preselect({ selection, qty, profile, leadId, dealId });
      setOutcome(res);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not create the quotation.');
    } finally {
      setLoading(false);
    }
  }

  const num = (k: string, v: string) => setProfile((p) => ({ ...p, [k]: v === '' ? undefined : Number(v) }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 560 }}>
      <p style={{ fontSize: 13, color: '#555', margin: 0 }}>
        Pick a model directly — no calculator. {calc === 'ss_tank'
          ? 'Capacity and grade come from the chosen SKU.'
          : 'State the customer profile so we can flag any capacity limits (it never blocks the quote).'}
      </p>

      <label style={lbl}>
        <span style={{ color: '#444', fontWeight: 500 }}>{calc === 'ss_tank' ? 'Tank SKU' : 'Model / Series'}</span>
        <select value={selection} onChange={(e) => setSelection(e.target.value)} style={inp} data-testid="pick-model-select">
          {options.length === 0 && <option value="">No priced options available</option>}
          {calc === 'ss_tank'
            ? (options as SsTankPreselectOption[]).map((o) => <option key={o.sku} value={o.sku}>{o.label}</option>)
            : (options as DhpPreselectOption[]).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>

      <label style={{ ...lbl, maxWidth: 140 }}>
        <span style={{ color: '#444', fontWeight: 500 }}>Quantity</span>
        <input type="number" min={1} max={50} value={qty}
          onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))} style={inp} data-testid="pick-model-qty" />
      </label>

      {calc === 'domestic_heat_pump' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label style={lbl}><span>Family members</span>
            <input type="number" min={0} value={String(profile.family_members ?? '')} onChange={(e) => num('family_members', e.target.value)} style={inp} /></label>
          <label style={lbl}><span>Washrooms</span>
            <input type="number" min={0} value={String(profile.washrooms ?? '')} onChange={(e) => num('washrooms', e.target.value)} style={inp} /></label>
          <label style={lbl}><span>Covered area (sq ft)</span>
            <input type="number" min={0} value={String(profile.covered_area ?? '')} onChange={(e) => num('covered_area', e.target.value)} style={inp} /></label>
          <label style={lbl}><span>Jacuzzi units</span>
            <input type="number" min={0} max={4} value={String(profile.jacuzzi ?? 0)} onChange={(e) => num('jacuzzi', e.target.value)} style={inp} /></label>
          <label style={lbl}><span>Usage</span>
            <select value={String(profile.usage_intensity ?? 'medium')} onChange={(e) => setProfile((p) => ({ ...p, usage_intensity: e.target.value }))} style={inp}>
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select></label>
          <label style={{ ...lbl, flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 22 }}>
            <input type="checkbox" checked={!!profile.kitchen_connected} onChange={(e) => setProfile((p) => ({ ...p, kitchen_connected: e.target.checked }))} />
            <span>Kitchen connected</span></label>
        </div>
      )}

      {error && <div role="alert" style={{ padding: 10, background: '#fde7e7', color: '#8b2222', borderRadius: 4, fontSize: 13 }}>{error}</div>}

      {!outcome && (
        <button type="button" onClick={handleCreate} disabled={loading || !selection} data-testid="pick-model-create"
          style={{ padding: '10px 18px', background: '#0b3d2e', color: 'white', border: 'none', borderRadius: 6, cursor: loading ? 'wait' : 'pointer', fontSize: 14, fontWeight: 600, alignSelf: 'flex-start' }}>
          {loading ? 'Creating…' : 'Create quotation'}
        </button>
      )}

      {outcome && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 14, background: '#f6faf8', border: '1px solid #d6e6dd', borderRadius: 8 }}>
          <div style={{ fontSize: 13, color: '#2a7a2a', fontWeight: 600 }}>Quotation ready.</div>
          {outcome.overCeiling && (
            <div role="alert" style={{ padding: 10, background: '#fef3c7', color: '#92400e', borderRadius: 4, fontSize: 13 }}>
              <strong>⚠ {outcome.overCeiling.disclaimer}</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {outcome.overCeiling.breaches.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          )}
          <ConvertToProposalButton calculatorKey={calc} quotationId={outcome.id} customerName={customerName} leadId={leadId} />
        </div>
      )}
    </div>
  );
}
